/* Roots — scroll-reveal for section heads (eyebrow → headline → lede).
   CSS lives in roots-editorial.css (html.r-reveal-on .r-rv). No-JS,
   reduced-motion, and unsupported browsers keep everything visible.
   Hardened: never arms while the tab is hidden (background tab /
   prerender defers IO callbacks), plus a scroll/resize fallback and
   6s / 12s timers so content can never stay invisible if IO misbehaves.

   First-paint flash fix: this script is deferred, so heads are already
   painted VISIBLE by the time it runs. Any group whose container is
   already in or above the viewport at arm time must NEVER be hidden —
   it stays solid with no fade (no visible→blank→fade flash). Only
   groups that start strictly BELOW the fold are hidden (.r-rv) and
   fade up via IO as the user scrolls to them. */
(function () {
  'use strict';

  /* Accessibility / capability bail-outs — elements stay fully visible. */
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;
  if (!Element.prototype.closest || !window.Map) return;

  /* Conservative list of section-HEAD elements only: eyebrows, section
     headlines, and section lede paragraphs. Never product cards, buttons,
     prices, or body content. */
  var SELECTORS = [
    /* eyebrows */
    '.r-ed .r-eyebrow',
    '.mdb__eyebrow',
    '.pw-eyebrow',
    '.sof-head .sof-eyebrow',
    '.cmp-head .cmp-eyebrow',
    /* section headlines */
    '.r-ed h1.r-serif',
    '.r-ed h2.r-serif',
    '.r-ed .r-h2',
    '.mdb__h1',
    '.mdb__h2',
    '.mdb__faith-h2',
    '.pw-intro h2',
    '.sof-head h2',
    '.cmp-head .cmp-title',
    /* section ledes */
    '.mdb__lede',
    '.pw-intro > p',
    '.sof-head > p',
    '.cmp-head .cmp-lede',
    '.rcomp__intro',
    '.rcompare__intro p',
    '.rmono__intro p',
    '.mh-history-intro',
    '.mh-helps-intro p'
  ].join(',');

  /* Fraction of the viewport height below which a group counts as
     already in-or-above the fold. rect.top < vh * THRESHOLD means the
     group is visible (or scrolled past) on first paint and must stay
     solid. rect.top >= vh * THRESHOLD means it starts below the fold
     and should be hidden + faded in. */
  var FOLD_RATIO = 0.95;

  function init() {
    var nodes;
    try { nodes = document.querySelectorAll(SELECTORS); } catch (err) { return; }
    if (!nodes.length) return;

    var roots = [];
    var groupMap = new Map();

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest('.glass-hero')) continue;     /* hero has its own load animation */
      if (el.closest('[class*="qz-"]')) continue;  /* never touch the quiz */
      var root = el.closest('.shopify-section') || el.closest('section') || el.parentElement;
      if (!root) continue;
      var group = groupMap.get(root);
      if (!group) { group = []; groupMap.set(root, group); roots.push(root); }
      group.push(el);
    }

    if (!roots.length) return;

    function viewportHeight() {
      return window.innerHeight || document.documentElement.clientHeight || 0;
    }

    /* Arm the CSS before hiding anything (safety net: without this html
       flag the .r-rv rules never apply and heads stay visible). */
    document.documentElement.classList.add('r-reveal-on');

    /* Gate hiding by viewport position at arm time. Because the script
       is deferred, every head is already painted solid right now. A
       group is only hidden if its container starts STRICTLY BELOW the
       fold; in-or-above-fold groups are left untouched (never given
       .r-rv) so they render solid from first paint with zero fade and
       zero flash. Only below-fold groups join the observed/failsafe set. */
    var vhArm = viewportHeight();
    var observed = [];          /* below-fold roots: hidden, observed, fade in */
    var observedMap = new Map(); /* root -> els, only for below-fold groups */

    roots.forEach(function (root) {
      var els = groupMap.get(root);
      var rect = root.getBoundingClientRect();
      /* In or above viewport (top < 95% of vh) — leave solid, no .r-rv. */
      if (rect.top < vhArm * FOLD_RATIO) return;
      /* Strictly below the fold — hide + stagger so it can fade up later. */
      observed.push(root);
      observedMap.set(root, els);
      els.forEach(function (el, idx) {
        el.classList.add('r-rv');
        el.style.transitionDelay = Math.min(idx * 90, 270) + 'ms';
      });
    });

    /* Nothing below the fold — every head is already solid, no work left. */
    if (!observed.length) return;

    /* From here on, groupMap/roots are narrowed to the below-fold set so
       every failsafe operates only on the hidden groups. */
    roots = observed;
    groupMap = observedMap;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /* Reveal when the group enters the viewport — or immediately if it
           already sits above it (deep links, restored scroll position). */
        if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return;
        reveal(entry.target);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

    roots.forEach(function (root) { io.observe(root); });

    /* Single reveal path shared by IO and every failsafe. */
    function reveal(root) {
      var els = groupMap.get(root);
      if (!els) return;
      els.forEach(function (el) { el.classList.add('r-rv-in'); });
      groupMap.delete(root);
      io.unobserve(root);
      if (!groupMap.size) teardown();
    }

    /* Failsafe 1 — manual in-view check on scroll/resize (passive,
       rAF-throttled). Belt-and-braces in case IO callbacks are deferred
       or dropped. Detached automatically once every group is revealed. */
    var ticking = false;
    function manualCheck() {
      ticking = false;
      if (!groupMap.size) return;
      var vh = viewportHeight();
      roots.forEach(function (root) {
        if (!groupMap.has(root)) return;
        var r = root.getBoundingClientRect();
        if (r.top < vh * 0.92 && r.bottom > 0) reveal(root);
      });
    }
    function onScrollResize() {
      if (ticking) return;
      ticking = true;
      (window.requestAnimationFrame || window.setTimeout)(manualCheck);
    }
    window.addEventListener('scroll', onScrollResize, { passive: true });
    window.addEventListener('resize', onScrollResize, { passive: true });

    /* Failsafe 2 — 6s after arming: reveal anything intersecting or above
       the current viewport. Below-fold groups keep their stagger for when
       they scroll in. */
    var t6 = setTimeout(function () {
      if (!groupMap.size) return;
      var vh = viewportHeight();
      roots.forEach(function (root) {
        if (!groupMap.has(root)) return;
        if (root.getBoundingClientRect().top < vh) reveal(root);
      });
    }, 6000);

    /* Failsafe 3 — 12s absolute: reveal EVERYTHING still hidden, so no
       content can be permanently invisible under any circumstance. */
    var t12 = setTimeout(function () {
      roots.forEach(function (root) { reveal(root); });
    }, 12000);

    function teardown() {
      window.removeEventListener('scroll', onScrollResize);
      window.removeEventListener('resize', onScrollResize);
      clearTimeout(t6);
      clearTimeout(t12);
    }
  }

  function start() {
    /* Loaded with defer, so the DOM is parsed — run immediately. */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  /* Hidden-tab guard — Chrome defers IO callbacks while a page is hidden
     (background tab, prerender). Never hide anything until visible. */
  if (document.visibilityState === 'hidden') {
    document.addEventListener('visibilitychange', function onVisible() {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onVisible);
      start();
    });
  } else {
    start();
  }
})();
